import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { fmtTRY, fmtGold, fmtDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Heart, Book, Download, Share2, Camera, MessageCircle, Gift, Play,
  MapPin, Calendar, Users, Sparkles, ChevronLeft, Feather,
} from "lucide-react";

const COVER = {
  couple: "Ayşe & Mehmet",
  date: "23 Ağustos 2026",
  location: "Ciragan Sarayı, İstanbul",
  photo_url: "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHx3ZWRkaW5nJTIwY291cGxlfGVufDB8fHx8MTc4NjcxNTIzMHww&ixlib=rb-4.1.0&q=85",
};

const PHOTOS = [
  "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?crop=entropy&cs=srgb&fm=jpg&h=600&q=70",
  "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&h=600&q=70",
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?crop=entropy&cs=srgb&fm=jpg&h=600&q=70",
  "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?crop=entropy&cs=srgb&fm=jpg&h=600&q=70",
  "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?crop=entropy&cs=srgb&fm=jpg&h=600&q=70",
  "https://images.unsplash.com/photo-1519657337289-077653f724ed?crop=entropy&cs=srgb&fm=jpg&h=600&q=70",
];

const MESSAGES = [
  { name: "Zeynep A.", text: "Mutluluklar dileriz, ömür boyu birlikte olun!", at: "20:14" },
  { name: "Ali Y.",    text: "Ellerinize sağlık, muhteşem bir geceydi 🌙", at: "20:31" },
  { name: "Fatma D.",  text: "Uzaktan da olsa yanınızdayız — canlı yayın harikaydı!", at: "20:47" },
  { name: "Emre S.",   text: "Bir yastıkta kocayın, ne mutlu size.", at: "21:02" },
  { name: "Ayşegül B.",text: "Yüzük çok yakıştı Ayşe’ciğim!", at: "21:15" },
  { name: "Onur T.",   text: "Yayına kaldık ve bir çeyrek altın gönderdik ✨", at: "21:33" },
  { name: "Selin M.",  text: "Ne kadar da güzel bir çift, iyi ki varsınız.", at: "21:52" },
  { name: "Sürpriz",   text: "Adınıza LÖSEV'e bağış yaptık, mutluluğunuz bereketli olsun.", at: "22:04" },
];

const GIFTS = [
  { name: "Zeynep A.", kind: "gold",     amount_kurus: 78000,  grams_mg: 3000, note: "Bir çeyrek altın" },
  { name: "Ali Y.",    kind: "cash",     amount_kurus: 250000,                 note: "Mutluluklar!" },
  { name: "Fatma D.",  kind: "coupon",   amount_kurus: 260000,                 note: "1 gr altın kuponu", partner: "Altınbaş Kuyumcu" },
  { name: "Emre S.",   kind: "donation", amount_kurus: 100000,                 note: "LÖSEV bağışı", cause: "LÖSEV" },
  { name: "Ayşegül B.",kind: "gold",     amount_kurus: 195000, grams_mg: 7000, note: "Tam altın" },
  { name: "Onur T.",   kind: "cash",     amount_kurus: 500000,                 note: "Yeni yuvanız için" },
];

const HIGHLIGHTS = [
  { title: "İlk dans",          time: "21:04", duration: "3:12" },
  { title: "Yemin töreni",      time: "20:47", duration: "8:26" },
  { title: "Pasta kesimi",      time: "21:38", duration: "1:44" },
  { title: "Çiçek atma",        time: "22:11", duration: "0:52" },
];

const KIND_LABEL = { cash: "Nakit", gold: "Altın", coupon: "Kupon", donation: "Bağış", message: "Mesaj" };

export default function MemoryDemo() {
  const { t } = useI18n();
  const [tab, setTab] = useState("photos");
  const printRef = useRef(null);

  const totals = useMemo(() => {
    const cash = GIFTS.reduce((s, g) => s + (g.amount_kurus || 0), 0);
    const gold = GIFTS.filter(g => g.kind === "gold").reduce((s, g) => s + (g.grams_mg || 0), 0);
    return { cash_kurus: cash, gold_mg: gold, gifts: GIFTS.length, messages: MESSAGES.length, guests: 142 };
  }, []);

  const shareLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${COVER.couple} · Anı Defteri`, text: "Bizim anılarımıza göz atın", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Bağlantı kopyalandı");
      }
    } catch { /* noop */ }
  };

  const downloadPDF = () => {
    const cardsHtml = GIFTS.map(g => `
      <div class="row">
        <span><strong>${g.name}</strong> · ${KIND_LABEL[g.kind]}${g.partner ? ` · ${g.partner}` : ""}${g.cause ? ` · ${g.cause}` : ""}</span>
        <span>${g.kind === "gold" ? (g.grams_mg / 1000).toFixed(2) + " gr" : "₺" + (g.amount_kurus / 100).toFixed(2)}</span>
      </div>`).join("");
    const msgsHtml = MESSAGES.map(m => `
      <div class="msg"><strong>${m.name}</strong> · <span class="mut">${m.at}</span><br/><em>“${m.text}”</em></div>`).join("");
    const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${COVER.couple} · Anı Defteri</title>
      <style>
        @page { size: A4; margin: 24mm; }
        body { font-family: Georgia, serif; color: #1a1a1a; line-height: 1.5; }
        h1 { font-size: 44px; margin: 0 0 4px; letter-spacing: -0.5px; }
        .sub { color: #666; margin-bottom: 32px; }
        .kpi { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 20px 0 28px; }
        .kpi div { border: 1px solid #eee; padding: 10px 12px; border-radius: 8px; }
        .kpi small { color: #888; text-transform: uppercase; letter-spacing: 2px; font-size: 9px; }
        .kpi b { font-family: 'Courier New', monospace; font-size: 18px; display: block; margin-top: 4px; }
        h2 { margin: 24px 0 10px; font-size: 22px; border-bottom: 1px solid #eee; padding-bottom: 6px; }
        .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #eee; }
        .msg { margin: 10px 0; padding: 8px 12px; background: #fafafa; border-left: 3px solid #d4af37; }
        .mut { color: #888; font-size: 12px; }
        .footer { margin-top: 32px; color: #999; font-size: 11px; text-align: center; }
      </style></head>
      <body>
        <h1>${COVER.couple}</h1>
        <div class="sub">${COVER.date} · ${COVER.location}</div>
        <div class="kpi">
          <div><small>Misafirler</small><b>${totals.guests}</b></div>
          <div><small>Mesajlar</small><b>${totals.messages}</b></div>
          <div><small>Hediyeler</small><b>${totals.gifts}</b></div>
          <div><small>Altın</small><b>${(totals.gold_mg / 1000).toFixed(2)} gr</b></div>
        </div>
        <h2>Tebrik Mesajları</h2>
        ${msgsHtml}
        <h2>Hediye Duvarı</h2>
        ${cardsHtml}
        <div class="footer">Heranida · Anı Defteri · Bu bir demo çıktısıdır.</div>
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`;
    const win = window.open("", "_blank", "noopener,noreferrer");
    if (win) {
      win.document.write(html);
      win.document.close();
      return;
    }
    // Fallback: download as HTML blob when popup is blocked
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${COVER.couple.replace(/[^\w-]+/g, "_")}_ani_defteri.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success("Anı defteri indirildi");
  };

  return (
    <div className="relative">
      <div className="sticky top-16 z-30 bg-primary/95 text-primary-foreground text-xs uppercase tracking-[0.35em] py-1.5 text-center font-medium" data-testid="memory-demo-banner">
        <span className="inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
          {t.demo_badge || "DEMO"} · Anı Defteri · Her ziyaretçi kendi oturumunu görür
        </span>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" ref={printRef}>
        <Button asChild variant="ghost" size="sm" className="gap-1 -ms-2 mb-4" data-testid="memory-demo-back">
          <Link to="/"><ChevronLeft className="w-4 h-4" /> Ana Sayfa</Link>
        </Button>

        {/* Cover */}
        <Card className="overflow-hidden mb-8 border-primary/20">
          <div className="relative aspect-[16/8]">
            <img src={COVER.photo_url} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/70 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 text-white">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-primary/90">
                <Feather className="w-3 h-3" /> Anı Defteri
              </div>
              <h1 className="font-serif text-5xl sm:text-7xl mt-3 gold-text drop-shadow-2xl">{COVER.couple}</h1>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/90">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {COVER.date}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {COVER.location}</span>
                <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {totals.guests} misafir</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button className="gap-2 rounded-full" onClick={downloadPDF} data-testid="memory-download">
            <Download className="w-4 h-4" /> PDF olarak indir
          </Button>
          <Button variant="outline" className="gap-2 rounded-full border-primary/40" onClick={shareLink} data-testid="memory-share">
            <Share2 className="w-4 h-4" /> Paylaş
          </Button>
          <Badge className="ms-auto bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 uppercase tracking-wider text-[10px]">Paket · 1 Ay Aktif</Badge>
        </div>

        {/* Totals strip */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          {[
            ["Misafir", totals.guests, Users],
            ["Mesaj", totals.messages, MessageCircle],
            ["Hediye", totals.gifts, Gift],
            ["Toplam Nakit", fmtTRY(totals.cash_kurus), Sparkles],
            ["Toplam Altın", fmtGold(totals.gold_mg), Heart],
          ].map(([label, val, Icon], i) => (
            <Card key={label} className="p-4" data-testid={`memory-stat-${i}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground"><Icon className="w-3 h-3" /> {label}</div>
              <div className="font-mono text-xl mt-1">{val}</div>
            </Card>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 max-w-lg">
            <TabsTrigger value="photos"   data-testid="mm-tab-photos"   className="gap-1"><Camera        className="w-3 h-3" /> Kareler</TabsTrigger>
            <TabsTrigger value="messages" data-testid="mm-tab-messages" className="gap-1"><MessageCircle className="w-3 h-3" /> Mesajlar</TabsTrigger>
            <TabsTrigger value="gifts"    data-testid="mm-tab-gifts"    className="gap-1"><Gift          className="w-3 h-3" /> Hediyeler</TabsTrigger>
            <TabsTrigger value="stream"   data-testid="mm-tab-stream"   className="gap-1"><Play          className="w-3 h-3" /> Yayın</TabsTrigger>
          </TabsList>

          <TabsContent value="photos" className="mt-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PHOTOS.map((src, i) => (
                <div key={i} className="relative aspect-[3/4] rounded-lg overflow-hidden ring-1 ring-primary/10 group animate-float-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`mm-photo-${i}`}>
                  <img src={src} alt={`Kare ${i + 1}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 p-2 text-[10px] uppercase tracking-widest text-white/90 bg-gradient-to-t from-secondary/80 to-transparent">
                    Kare · {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-5">
            <Card className="p-5">
              <div className="relative ps-6">
                <div className="absolute inset-y-0 start-2 w-px bg-border" />
                {MESSAGES.map((m, i) => (
                  <div key={i} className="relative mb-6 last:mb-0 animate-float-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`mm-msg-${i}`}>
                    <div className="absolute -start-4 top-2 w-3 h-3 rounded-full bg-primary/50 ring-4 ring-background" />
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-serif text-primary/90 text-base">{m.name}</span>
                      <span className="text-muted-foreground font-mono">{m.at}</span>
                    </div>
                    <p className="text-sm mt-1 text-foreground/90">&ldquo;{m.text}&rdquo;</p>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="gifts" className="mt-5">
            <div className="grid sm:grid-cols-2 gap-3">
              {GIFTS.map((g, i) => (
                <Card key={i} className="p-4 animate-float-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`mm-gift-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-serif text-lg">{g.name}</div>
                      {g.note && <div className="text-xs text-muted-foreground mt-0.5">&ldquo;{g.note}&rdquo;</div>}
                      {g.partner && <div className="text-[10px] uppercase tracking-wider text-amber-400 mt-1">Partner: {g.partner}</div>}
                      {g.cause   && <div className="text-[10px] uppercase tracking-wider text-emerald-300 mt-1">Kurum: {g.cause}</div>}
                    </div>
                    <div className="text-end">
                      <div className="text-[10px] uppercase tracking-wider text-primary/70">{KIND_LABEL[g.kind]}</div>
                      <div className="font-mono text-sm">{g.kind === "gold" ? fmtGold(g.grams_mg) : fmtTRY(g.amount_kurus)}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="stream" className="mt-5">
            <div className="grid sm:grid-cols-2 gap-3">
              {HIGHLIGHTS.map((h, i) => (
                <Card key={h.title} className="overflow-hidden" data-testid={`mm-hl-${i}`}>
                  <div className="relative aspect-video bg-secondary">
                    <div className="absolute inset-0" style={{
                      background: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.25), transparent 60%), #0A1128"
                    }} />
                    <div className="absolute inset-0 grid place-items-center">
                      <button className="w-14 h-14 rounded-full bg-primary/20 ring-1 ring-primary/50 grid place-items-center pulse-gold" aria-label="oynat">
                        <Play className="w-6 h-6 text-primary" />
                      </button>
                    </div>
                    <div className="absolute bottom-2 end-2 text-[10px] font-mono px-2 py-1 rounded bg-secondary/70 ring-1 ring-white/10">{h.duration}</div>
                  </div>
                  <div className="p-3 flex items-center justify-between">
                    <div className="font-serif">{h.title}</div>
                    <div className="text-xs text-muted-foreground font-mono">{h.time}</div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-10 p-6 rounded-lg bg-muted/30 text-xs text-muted-foreground flex items-start gap-3">
          <Book className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div>
            <div className="text-foreground font-serif text-base mb-1">Anı defteri paketleri</div>
            Etkinlik biter bitmez Heranida sizin için bu sayfayı oluşturur. Paket süresi boyunca (1 hafta, 1 ay veya uzun süreli) misafirlerinizle paylaşabilir, PDF olarak arşivleyebilirsiniz. Süre dolduğunda uzatma veya kalıcı arşive taşıma seçenekleri sunulur.
          </div>
        </div>
      </div>
    </div>
  );
}
